import React, { useState, useEffect } from 'react'
import miningService from '../services/miningService'
import { useLanguage } from '../i18n'

// 时段状态指示器
const TimeSlotIndicator = ({ timeInfo, status, t }) => {
  if (!timeInfo) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-300">{t('miningDashboard.loadingTimeSlots')}</span>
      </div>
    )
  }

  const isActive = status === 'active'
  const isSlotA = timeInfo.currentSlot.slot === 'A'

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-8 shadow-2xl">
      <div className="text-center mb-6">
        <h3 className="text-3xl font-bold mb-2">{t('miningDashboard.miningSequence')}</h3>
        <p className="text-gray-300">{t('miningDashboard.dualTimeSystem')}</p>
      </div>

      {/* 时段指示器 */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* 第一序列 */}
        <div className={`relative p-6 rounded-2xl transition-all duration-300 ${
          isSlotA && isActive 
            ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 shadow-lg' 
            : 'bg-gradient-to-br from-gray-700/20 to-gray-600/20 border-2 border-gray-600/30'
        }`}>
          <div className="text-center">
            <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
              isSlotA && isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-500'
            }`}></div>
            <h4 className="text-xl font-semibold mb-1">{t('miningDashboard.firstSequence')}</h4>
            <p className="text-sm text-gray-300">08:00 - 19:59</p>
            {isSlotA && isActive && (
              <div className="mt-2 text-green-400 text-sm font-medium">
                🔥 {t('miningDashboard.inProgress')}
              </div>
            )}
          </div>
        </div>

        {/* 第二序列 */}
        <div className={`relative p-6 rounded-2xl transition-all duration-300 ${
          !isSlotA && isActive 
            ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-blue-500/50 shadow-lg' 
            : 'bg-gradient-to-br from-gray-700/20 to-gray-600/20 border-2 border-gray-600/30'
        }`}>
          <div className="text-center">
            <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
              !isSlotA && isActive ? 'bg-blue-500 animate-pulse' : 'bg-gray-500'
            }`}></div>
            <h4 className="text-xl font-semibold mb-1">{t('miningDashboard.secondSequence')}</h4>
            <p className="text-sm text-gray-300">20:00 - 07:59</p>
            {!isSlotA && isActive && (
              <div className="mt-2 text-blue-400 text-sm font-medium">
                🌙 {t('miningDashboard.inProgress')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 状态信息 */}
      <div className="text-center">
        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
          isActive 
            ? 'bg-gradient-to-r from-green-500/20 to-blue-500/20 text-green-400 border border-green-500/30' 
            : 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/30'
        }`}>
          <div className={`w-2 h-2 rounded-full mr-2 ${
            isActive ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
          }`}></div>
          {status.message}
        </div>

        {/* 倒计时 */}
        <div className="bg-black/30 rounded-2xl p-4 border border-gray-600/30">
          <p className="text-gray-300 text-sm mb-2">{t('miningDashboard.nextSlotIn')}</p>
          <div className="text-2xl font-mono font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            {timeInfo.countdown}
          </div>
        </div>
      </div>
    </div>
  )
}

// 交互按钮组件
const InteractButton = ({ 
  isConnected, 
  walletAddress, 
  timeInfo, 
  status, 
  userInteractions, 
  onInteract, 
  isLoading,
  t 
}) => {
  const hasInteractedToday = userInteractions && userInteractions.length > 0
  const currentSlotInteraction = userInteractions?.find(
    interaction => interaction.time_slot === timeInfo?.currentSlot.slot
  )

  const canInteract = isConnected && status.status === 'active' && !currentSlotInteraction

  const getButtonState = () => {
    if (!isConnected) {
      return {
        disabled: true,
        text: t('miningDashboard.pleaseConnectWallet'),
        className: 'bg-gray-600 text-gray-400 cursor-not-allowed'
      }
    }

    if (status.status !== 'active') {
      return {
        disabled: true,
        text: t('miningDashboard.waitingForSlot'),
        className: 'bg-yellow-600/30 text-yellow-400 border border-yellow-500/30 cursor-not-allowed'
      }
    }

    if (currentSlotInteraction) {
      return {
        disabled: true,
        text: t('miningDashboard.recharging'),
        className: 'bg-red-600/30 text-red-400 border border-red-500/30 cursor-not-allowed'
      }
    }

    return {
      disabled: false,
      text: isLoading ? t('miningDashboard.interacting') : t('miningDashboard.startMining'),
      className: 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300'
    }
  }

  const buttonState = getButtonState()

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h4 className="text-xl font-semibold mb-4 text-center">{t('miningDashboard.miningInteraction')}</h4>
      
      <button
        onClick={() => canInteract && onInteract()}
        disabled={buttonState.disabled || isLoading}
        className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all duration-300 ${buttonState.className}`}
      >
        {buttonState.text}
      </button>

      {/* 状态信息 */}
      <div className="mt-4 space-y-2 text-sm">
        {isConnected && (
          <div className="flex justify-between text-gray-300">
            <span>{t('miningDashboard.walletStatus')}:</span>
            <span className="text-green-400">{t('miningDashboard.connected')}</span>
          </div>
        )}
        
        {timeInfo && (
          <div className="flex justify-between text-gray-300">
            <span>{t('miningDashboard.currentSlot')}:</span>
            <span className={status.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>
              {timeInfo.currentSlot.name}
            </span>
          </div>
        )}
        
        {currentSlotInteraction && (
          <div className="flex justify-between text-gray-300">
            <span>{t('miningDashboard.todayInteraction')}:</span>
            <span className="text-red-400">{t('miningDashboard.completed')}</span>
          </div>
        )}
        
        {hasInteractedToday && !currentSlotInteraction && (
          <div className="flex justify-between text-gray-300">
            <span>{t('miningDashboard.todayInteraction')}:</span>
            <span className="text-blue-400">{t('miningDashboard.waitingNextSlot')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// 用户交互记录组件
const UserInteractions = ({ userInteractions, t }) => {
  if (!userInteractions || userInteractions.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h4 className="text-xl font-semibold mb-4">{t('miningDashboard.todayRecords')}</h4>
        <p className="text-gray-400 text-center py-4">{t('miningDashboard.noRecords')}</p>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h4 className="text-xl font-semibold mb-4">{t('miningDashboard.todayRecords')}</h4>
      <div className="space-y-3">
        {userInteractions.map((interaction, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                interaction.time_slot === 'A' ? 'bg-green-500' : 'bg-blue-500'
              }`}></div>
              <span className="font-medium">
                {interaction.time_slot === 'A' ? t('miningDashboard.firstSequence') : t('miningDashboard.secondSequence')}
              </span>
            </div>
            <span className="text-gray-400 text-sm">
              {new Date(interaction.created_at).toLocaleTimeString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// 主仪表盘组件
const MiningDashboard = ({ isConnected, walletAddress, onConnect }) => {
  const { t } = useLanguage()
  const [timeInfo, setTimeInfo] = useState(null)
  const [status, setStatus] = useState({ status: 'unknown', message: t('miningDashboard.loading') })
  const [userInteractions, setUserInteractions] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [serverConnected, setServerConnected] = useState(false)

  // 加载数据
  useEffect(() => {
    loadData()
    
    // 设置定时器更新倒计时
    const timer = setInterval(() => {
      updateCountdown()
    }, 1000)

    // 设置定时器刷新数据
    const dataRefreshTimer = setInterval(() => {
      loadData(false) // 不显示加载状态
    }, 30000) // 30秒刷新一次

    return () => {
      clearInterval(timer)
      clearInterval(dataRefreshTimer)
    }
  }, [isConnected, walletAddress])

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true)
      
      // 检查服务器连接（但不阻止加载，会使用模拟数据）
      const connected = await miningService.checkConnection()
      setServerConnected(connected || true) // 允许使用模拟数据
      
      // 获取时段信息（会自动使用模拟数据）
      const timeData = await miningService.getTimeInfo()
      setTimeInfo(timeData)
      
      // 更新状态
      const newStatus = miningService.getSlotStatus(timeData)
      setStatus(newStatus)

      // 如果已连接钱包，获取用户交互记录
      if (isConnected && walletAddress) {
        try {
          const interactions = await miningService.getUserInteractions(walletAddress)
          setUserInteractions(interactions.interactions)
        } catch (error) {
          console.error('获取用户交互记录失败:', error)
          setUserInteractions([])
        }
      }

    } catch (error) {
      console.error('加载数据失败:', error)
      setStatus({ status: 'error', message: t('miningDashboard.dataLoadFailed') })
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }

  const updateCountdown = () => {
    if (timeInfo) {
      const newStatus = miningService.getSlotStatus(timeInfo)
      setStatus(newStatus)
    }
  }

  const handleInteract = async () => {
    if (!isConnected || !walletAddress) return

    setIsLoading(true)
    try {
      const result = await miningService.interact(walletAddress)
      
      if (result.success) {
        // 重新加载数据
        await loadData(false)
        
        // 显示成功消息
        alert(result.data.message)
      }
    } catch (error) {
      console.error('交互失败:', error)
      alert(error.message || t('miningDashboard.interactionFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* 时段指示器 */}
      <TimeSlotIndicator timeInfo={timeInfo} status={status} t={t} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 交互按钮 */}
        <InteractButton
          isConnected={isConnected}
          walletAddress={walletAddress}
          timeInfo={timeInfo}
          status={status}
          userInteractions={userInteractions}
          onInteract={handleInteract}
          isLoading={isLoading}
          t={t}
        />

        {/* 用户交互记录 */}
        <UserInteractions userInteractions={userInteractions} t={t} />
      </div>

      {/* 服务器状态 */}
      <div className="text-center text-gray-400 text-sm">
        <p>{t('miningDashboard.serverTime')}: {timeInfo?.serverTime || '--:--:--'}</p>
        <p>{t('miningDashboard.serverStatus')}: <span className="text-green-400">{t('miningDashboard.normal')}</span></p>
      </div>
    </div>
  )
}

export default MiningDashboard
